import type { User } from '../types'
import { apiFetch } from './core'

export interface LoginResult {
  token?: string
  mfa_required: boolean
  mfa_token?: string
  mfa_setup_required: boolean
  setup_token?: string
  password_change_required: boolean
  password_change_token?: string
}

export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export async function changeRequiredPassword(password_change_token: string, new_password: string): Promise<string> {
  const res = await fetch('/api/auth/change-required-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password_change_token, new_password }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  const data = await res.json()
  return data.token
}

export async function mfaVerify(mfa_token: string, code: string): Promise<string> {
  const res = await fetch('/api/auth/mfa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfa_token, code }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  const data = await res.json()
  return data.token
}

export function setToken(token: string): void {
  localStorage.setItem('token', token)
}

export function clearCredentials(): void {
  localStorage.removeItem('token')
}

export function hasCredentials(): boolean {
  return !!localStorage.getItem('token')
}

export async function getMe(): Promise<User> {
  return apiFetch<User>('/api/auth/me')
}

export async function setupMfa(): Promise<{ secret: string; qr_code: string }> {
  return apiFetch('/api/auth/mfa/setup')
}

export async function activateMfa(secret: string, code: string): Promise<string | null> {
  const data = await apiFetch<{ message: string; token?: string }>('/api/auth/mfa/activate', {
    method: 'POST',
    body: JSON.stringify({ secret, code }),
  })
  return data?.token ?? null
}

export async function disableMfa(password: string): Promise<void> {
  return apiFetch('/api/auth/mfa', {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  })
}

export async function changePassword(current_password: string, new_password: string): Promise<void> {
  return apiFetch('/api/auth/me/password', {
    method: 'PUT',
    body: JSON.stringify({ current_password, new_password }),
  })
}

export async function patchMe(data: { preferred_language?: string }): Promise<User> {
  return apiFetch<User>('/api/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}
